/**
 * root.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import bluebird from "bluebird";
import { CacheProvider } from "@emotion/react";
import createCache, { EmotionCache } from "@emotion/cache";
//import { createBrowserHistory, History } from "history";
import * as React from "react";
import { Navigate, Route, RouterProvider, createBrowserRouter, createRoutesFromElements } from "react-router-dom";

import { AccountScopes, Membership, PermissionLevel } from "./models/auth";

import { AuthContext } from "./auth/auth.context";
import { AuthLandingContainer } from "./auth/authLanding.component";
import { LoginContainer } from "./auth/login.component";
import { ChatContainerWrapper } from "./chat/chatContainer";
import { LoadingView } from "./components/loading.component";
import { MeetingsContainer } from "./streaming/meetingsContainer.component";
import { NotificationPermissionRequestComponent } from "./notifications/permissionRequest.component";
import { NewVersionNotificationComponent } from "./components/newVersion.component";
import { AccountContainer } from "./accounts/accountContainer.component";
import { PasswordRequestContainer } from "./auth/pwRequest.component";
import { PasswordResetWrapper } from "./auth/pwReset.component";
import { ProcessInviteWrapper } from "./auth/onboardInvite.component";
import { ProfileContainerWrapper } from "./profile/profileContainer.component";
import { IncomingCallRequestComponent } from "./streaming/incomingCallRequest.component";

import { Brand } from "./models/brand";
import { ErrorCode, UnauthError } from "./models/error";
import { authServiceFactory } from "./services/auth.service";
import { cryptoServiceFactory } from "./services/crypto.service";
import { groupServiceFactory } from "./services/group.service";
import { workerServiceFactory, WorkerServiceDelegate } from "./services/worker.service";
import { conferenceServiceFactory } from "./services/conferencing.service";
import { OAuthComponent } from "./auth/oauth.component";

interface RootState extends AuthContext {
  loading: boolean,
  reloadNeeded: boolean,
  groupCallName: string
}

interface RootProps {
  cspNonce?: string | null
}
export class Root extends React.Component<RootProps, RootState> implements WorkerServiceDelegate {

  private noteAudio: React.Ref<HTMLAudioElement>;
  private callAudio: React.Ref<HTMLAudioElement>;
  private cacheProvider: EmotionCache;
  private timer: NodeJS.Timeout | null;
  private pollSWTimer: NodeJS.Timeout | null;
  private router?: any;
  private incomingCallData: null | {accountID: string, groupID: string, groupname: string};

  constructor(props: RootProps) {
    super(props);
    this.noteAudio = React.createRef<HTMLAudioElement>();
    this.callAudio = React.createRef<HTMLAudioElement>();
    this.cacheProvider = createCache({
      key: "css",
      nonce: props.cspNonce || undefined,
      prepend: true
    });
    this.timer = null;
    this.pollSWTimer = null;
    this.incomingCallData = null;

    this.finishedLogin = this.finishedLogin.bind(this);
    this.setAccount = this.setAccount.bind(this);
    this.enableNotifications = this.enableNotifications.bind(this);
    this.visibilityChange = this.visibilityChange.bind(this);
    this.can = this.can.bind(this);
    this.tz = this.tz.bind(this);
    this.willCloseTab = this.willCloseTab.bind(this);
    this.ignoreIncomingCall = this.ignoreIncomingCall.bind(this);
    this.joinIncomingCall = this.joinIncomingCall.bind(this);

    const domain = authServiceFactory().domain;
    this.state = {
      domain: domain,
      brand: domain.indexOf("glyphapp") < 0 ? Brand.WORKSPACE : Brand.GLYPH,
      loading: true,
      reloadNeeded: false,
      access_token: "",
      membership: null,
      memberships: [],
      groupCallName: "",
      finishedLogin: this.finishedLogin,
      setAccount: this.setAccount,
      can: this.can,
      tz: this.tz
    };
  }

  async componentDidMount (): Promise<void> {
    document.addEventListener("visibilitychange", this.visibilityChange);
    document.addEventListener("beforeunload", this.willCloseTab);

    try {
      const authService = authServiceFactory();

      this.setState({loading: true});
      const workerService = workerServiceFactory();
      workerService.delegate = this;

      const [registration, userID,] = await bluebird.all([
        workerService.registerWorker(),
        authService.init(),
        cryptoServiceFactory().init()
      ]);

      if (registration?.active && (registration?.installing || registration?.waiting)) {
        this.setState({reloadNeeded: true});
      }
      registration?.addEventListener("updatefound", () => {
        if (registration.active) {
          this.setState({reloadNeeded: true});
        } else {
          console.log("not showing update installing:", registration);
        }
      });

      if (!userID) { return; }

      const pathname = window.location.pathname;
      const matches =  /^\/group\/(\w+)/.exec(pathname);
      const groupID = matches && matches[1];

      const data = await bluebird.props({
        memberships: authService.getAccountMemberships(),
        subscription: workerService.alreadyHaveNotificationPermission ?
        workerService.subscribe(registration)
          .then(null, err => {
            console.error("Couldn't register or subscribe:", err);
            return undefined;
          }) :
          bluebird.resolve(undefined),
        persisted: enablePersistentStorageAsNeeded()
      });

      if (authService.currentAccountMembership) {
        const groupService = groupServiceFactory();
        await groupService.init(
          authService.currentAccountMembership.userID,
          data.subscription, true)
        .then(null, err => {
          if (err instanceof UnauthError && err.code === ErrorCode.revoked) {
            this.setState({access_token: ""});
            authService.didLogout();
            groupService.didLogout();
            workerService.didLogout();
            if (pathname.startsWith("/group") ||
                pathname.startsWith("/authorize") ||
                pathname.startsWith("/account") ||
                pathname.startsWith("/settings")) {
              window.location.href = `/login?next=${window.location.pathname}`;
            }
          } else {
            console.error(typeof err, err);
          }
        });
        if (groupID) {
          // resolve group to see if it's in this account, switch accounts if possible
          const g = await groupService.getLocalGroup(groupID);
          if (g && g.accountID !== authService.currentAccountMembership.accountID) {
            const m = data.memberships.find(mem => mem.accountID === g.accountID);
            if (m) {
              authService.currentAccountMembership = m;
            }
          }
        }
        workerService.didLoadAuthed();
        this.pollSWTimer = setInterval(() => {
          fetch("/ping.txt");
        }, 25000);
      } else {
        console.warn("missing currentAccountMembership");
      }

      this.setState({
        access_token: authService.accessToken,
        membership: authService.currentAccountMembership || null,
        memberships: data.memberships
      });

    } catch (err) {
      if (err instanceof UnauthError && err.code === ErrorCode.revoked) {
        this.setState({access_token: ""});
        authServiceFactory().didLogout();
        groupServiceFactory().didLogout();
        workerServiceFactory().didLogout();
      } else {
        console.warn("Couldn't get account info:", (err as Error).message, err);
      }
    } finally {
      this.setState({loading: false});
    }
  }

  componentWillUnmount (): void {
    document.removeEventListener("visibilitychange", this.visibilityChange);
    workerServiceFactory().destroy();
    if (this.pollSWTimer) { clearInterval(this.pollSWTimer); }
    this.pollSWTimer = null;
  }

  private visibilityChange () {
    const workerService = workerServiceFactory();
    if (document.visibilityState === "visible") {
      workerService.didShow();
      workerService.updateWorker();
    } else {
      workerService.didHide();
    }
  }

  private willCloseTab(): void {
    workerServiceFactory().willUnload();
  }

  didReceiveNewMessage() {
    if (this.noteAudio) {
      try {
        (this.noteAudio as React.RefObject<HTMLAudioElement>).current?.play();
      } catch (err) {
        console.error("unable to play notification sound:", err);
      }
    }
  }

  didStartHuddle() {
    // we need to inform the user that the huddle in question wants them to join
    if (!this.callAudio) { return; }
    try {
      (this.callAudio as React.RefObject<HTMLAudioElement>).current?.play();
      this.timer = setTimeout(() => {
        this.stopAudio();
        this.timer = null;
      }, 30000);
    } catch (err) {
      console.error("unable to play call audio:", err);
    }
  }

  async navigateToGroup(accountID: string, groupID: string) {
    await this.jumpToAccount(accountID);
    this.router?.navigate(`/group/${groupID}`);
  }
  async doHuddleJoin(accountID: string, groupID: string) {
    await this.jumpToAccount(accountID);
    this.router?.navigate(`/group/${groupID}/huddle`);
    this.hideIncomingCall();
  }

  didAnswerHuddle() {
    this.stopAudio();
  }

  stopAudio() {
    try {
      const audio = (this.callAudio as React.RefObject<HTMLAudioElement>).current;
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    } catch (err) {
      console.error("Error stopping audio:", err);
    } finally {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    }
  }
  showIncomingCall(accountID: string, groupID: string, groupname: string): void {
    this.incomingCallData = {accountID, groupID, groupname};
     this.setState({groupCallName: groupname});
     console.log("showing incoming call UI");
  }
  hideIncomingCall(): void {
    console.log("hiding incoming call UI");
    this.setState({groupCallName: ""});
    this.incomingCallData = null;
    this.stopAudio();
  }
  async joinIncomingCall(): Promise<void> {
    this.setState({groupCallName: ""});
    this.stopAudio();
    if (!this.incomingCallData) { return; }
    await this.jumpToAccount(this.incomingCallData.accountID);
    this.router?.navigate(`/group/${this.incomingCallData.groupID}/huddle`);
  }

  async jumpToAccount(accountID: string) {
    if (this.state.membership && this.state.membership.accountID === accountID) {
      return;
    }
    const membership = this.state.memberships.find(m => m.accountID === accountID);
    if (!membership) { return; }
    await this.setAccount(membership);
    await bluebird.delay(600);
  }

  async ignoreIncomingCall(): Promise<void> {
    this.setState({groupCallName: ""});
    this.stopAudio();
    try {
      if (this.incomingCallData) {
        const accountID = this.incomingCallData.accountID;
        const groupID = this.incomingCallData.groupID;
        await conferenceServiceFactory().declineRoom(accountID, groupID, groupServiceFactory().deviceID);
      }
    } catch (err) {
      console.error("Error declining room:", err);
    }
  }

  private async setAccount(membership: Membership | null): Promise<void> {
    if (this.state.membership?._id === membership?._id) { return; }
    const authService = authServiceFactory();
    const groupService = groupServiceFactory();
    try {
      authService.currentAccountMembership = membership;
      if (membership) {
        if (!groupService.deviceID) {
          await groupService.init(membership.userID, undefined, true);
        }
        workerServiceFactory().didChangeAccount(
          membership?._id, membership?.accountID, membership.userID);
      } else {
        workerServiceFactory().didChangeAccount("", "", "");
      }

      this.setState({
        membership: membership,
        access_token: authService.accessToken
      });
    } catch (err) {
      console.error("Error changing accounts:", err);
    }
  }

  private can (level: PermissionLevel, perm: keyof AccountScopes): boolean {
    if (!this.state.membership) { return false; }
    const curPerm = this.state.membership.perms[perm] || PermissionLevel.NONE;
    return (curPerm & level) === level;
  }
  private tz (): string {
    return this.state.membership?.timezone || "";
  }

  async finishedLogin (): Promise<any> {
    const authService = authServiceFactory();
    this.setState({
      access_token: authService.accessToken
    });

    const memberships = await authService.getAccountMemberships(true);

    if (!authService.currentAccountMembership) {
      return bluebird.resolve(null);
    }
    const userID = authService.currentAccountMembership.userID;
    this.setState({
      membership: authService.currentAccountMembership,
      memberships: memberships
    });
    workerServiceFactory().didLogin(userID);
    const groupService = groupServiceFactory();
    return await groupService.init(userID, undefined, true)
    .then(null, err => {
      console.error("Error setting up groupService:", err);
    });
  }

  async enableNotifications(): Promise<any> {
    const workerService = workerServiceFactory();
    if ("PushManager" in window) {
      await workerService.askNotificationPermission();
      // only get here if we got permission
      //const registration = await notificationService.registerPushWorker();
      const registration = await navigator.serviceWorker.getRegistration("/");
      await workerService.subscribe(registration);
    } else if ((window as any).safari) {
      const deviceID = groupServiceFactory().deviceID;
      await workerService.askSafariNotificationPermission(this.state.access_token, deviceID);
    }
  }

  private getRouter(): any {
    const router = createBrowserRouter(createRoutesFromElements(
      <>
        <Route path="/login" element={<LoginContainer />} />
        <Route path="/logout" element={<LogoutComponent />} />
        <Route path="/invited" element={ <ProcessInviteWrapper />} />
        <Route path="/pwrequest" element={<PasswordRequestContainer />} />
        <Route path="/pwreset/:token" element={<PasswordResetWrapper />} />
        <Route path="/authlanding/:token?" element={<AuthLandingContainer />} />
        <Route path="/meeting/*" element={<MeetingsContainer />} />
        <Route path="/" element={<LandingComponent />} />
        <Route path="/authorize" element={<OAuthComponent />} />

        {this.renderRouteDefs()}
      </>
    ));
    this.router = router;
    return router;
  }
  render(): React.ReactElement<any, any> {
    if (this.state.loading) {
      return <LoadingView />;
    }
    
    const router = this.getRouter();

    return <CacheProvider value={this.cacheProvider}>
      <AuthContext.Provider value={this.state}>
        <RouterProvider router={router} />

        {this.state.access_token &&
          <NotificationPermissionRequestComponent
            onDoEnableNotifications={this.enableNotifications} />}
        <NewVersionNotificationComponent
          open={this.state.reloadNeeded}
          onClose={() => this.setState({reloadNeeded: false})} />
        <IncomingCallRequestComponent
          open={this.state.groupCallName !== ""}
          callName={this.state.groupCallName}
          ignoreCall={this.ignoreIncomingCall}
          joinCall={this.joinIncomingCall} />
        <audio ref={this.noteAudio} src="/aud/me-too-603.ogg" />
        <audio ref={this.callAudio} src="/aud/mercuryRingtone.ogg" loop/>
      </AuthContext.Provider>
    </CacheProvider>;
  }

  renderRouteDefs(): Array<React.ReactElement<any, any>> {
    const paths: Array<RouteInfo> = [
      {key: "group", path: "/group/*", redirectNext: "/group", element: <ChatContainerWrapper />},
      {key: "admin", path: "/admin/*", redirectNext: "/admin", element: <AccountContainer />},
      {key: "settings", path: "/settings/*", redirectNext: "/settings", element: <ProfileContainerWrapper />},
    ];

    if (!this.state.access_token) {
      console.log("generating redirect routes");
      return paths.map(route =>
        <Route key={route.key} path={route.path} element={
          <Navigate to={`/login?next=${encodeURIComponent(route.redirectNext)}`} />
        } />);
    }

    return paths.map(route => <Route {...route} />);
  }
}

function LandingComponent (): React.ReactElement {
  const authContext = React.useContext(AuthContext);

  if (authContext.access_token) {
    return <Navigate to="/group/" />;
  }
  return <Navigate to="/login/" />;
}

interface RouteInfo {
  key: string,
  path: string,
  redirectNext: string,
  element: React.ReactNode
}

export function LogoutComponent (): React.ReactElement<any, any> {
  const authService = authServiceFactory();
  authService.logout();
  return <Navigate to="/" />;
}

async function enablePersistentStorageAsNeeded(): Promise<void> {
  try {
    if (!navigator.storage || !navigator.storage.persisted as any) {
      return;
    }
    if (await navigator.storage.persisted()) {
      return;
    }
    if (await navigator.storage.persist()) {
      console.log("set persistent store");
    } else {
      console.warn("failed setting persistent store");
    }
  } catch (err) {
    console.error("Error enabling persistent storage:", err);
  }
}

